/*
INTELLECTUAL PROPERTY NOTICE
© Kaleida Ltd. All rights reserved.
This file contains Protected Intellectual Property owned by Kaleida Ltd and forms part of an application developed by Kaleida within the Platform.
Protected Intellectual Property may only be used as incorporated within applications developed by Kaleida within the Platform and must not be used, reproduced, adapted, or incorporated into any application developed by or on behalf of the Customer (explicitly not developed by Kaleida), whether or not such application forms part of the Platform.
For the avoidance of doubt, the inclusion of any application within the Platform does not of itself grant any right to use Protected Intellectual Property in that application unless that application was developed by Kaleida.
*/

// IP-NOTICE: PROTECTED
import { Component, ContentChild, forwardRef, Input, OnChanges, OnInit, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { ReactiveFormHelperService } from 'app/shared/helpers/reactive-form-helper.service';
import { IAutoCompleteAddListModel } from 'app/shared/models/auto-complete-add-list.model';
import { SnackBarService } from 'app/shared/services/snack-bar.service';
import { Observable } from 'rxjs';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import { map, startWith } from 'rxjs/operators';


@Component({
    template: '',
    standalone: false
})
export abstract class AutoCompleteAddListComponentBase implements ControlValueAccessor, OnInit, OnChanges {
  @Input() enabled = true;
  @Input() allowAdd = true;
  @Input() lookupData: IAutoCompleteAddListModel[];
  @Input() listItemTypeDisplay: string;
  @Input() listItemTypeDisplaySingular: string;
  @Input() deleteRequiresConfirmation: boolean = false;
  @Input() deleteConfirmationMessage = 'Are you sure you want to delete this item?';
  @Input() customNoItemsToDisplayMessage = null;  // optional override
  @Input() customListHeader = null;  // optional override
  @Input() customResultTemplate: TemplateRef<any>;
  @Input() addIconTitle: string = '';
  @Input() deleteIconTitle: string = '';

  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger;
  @ContentChild(TemplateRef) itemTemplate: TemplateRef<any>;

  filteredOptions: Observable<IAutoCompleteAddListModel[]>;

  dataForm: FormGroup;

  propagateChange: any = () => {};
  onTouched: any = () => {};

  myWrittenValue: any[] = [];


  get lookup(): AbstractControl {
    return this.dataForm.controls['lookup'];
  }

  constructor(
    private fb: FormBuilder,
    private formHelperService: ReactiveFormHelperService,
    protected dialog: MatDialog,
    private snackBar: SnackBarService
  ) {
    this.dataForm = fb.group(
      {
        lookup: [],
      },
    );
  }


  ngOnInit(): void {
    // TODO: [Refactor][dpb][2023-03-06] the non string value could be handled more elegantly with 2 separate handlers of the value changes
    // TODO: [lw][2023-02-08] check solution with damien - itemSelected$ not used
    // see https://stackoverflow.com/questions/62022870/how-to-stop-material-dropdown-autocomplete-selection-from-triggering-another-sea

    // If it's an object, it's been selected.
    // also don't allow selecting 'null'
    // const itemSelected$ = this.lookup.valueChanges.pipe(
    //   filter(val => typeof val === 'object' && val !== null),
    // );
    // // // If it's a string, it's been typed into the input
    // const itemTyped$ = this.lookup.valueChanges.pipe(
    //   filter(val => typeof val === 'string'),
    // );

    // this.filteredOptions = itemTyped$.pipe(
    //   //startWith(''),
    //   map((value) => this._filter(value /*|| ''*/))
    // );

    this.filteredOptions = this.lookup.valueChanges.pipe(
      startWith(''),
      map((value) => this._filter(value || ''))
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.lookupData && this.lookupData) {
      this.lookup.setValue('');
    }

    if(changes.enabled) {
      if(this.enabled) {
        this.lookup.enable();
      } else {
        this.lookup.disable();
      }
    }

  }

  get selectedItems(): IAutoCompleteAddListModel[]{
    if(!this.lookupData) {
      return [];
    }

    return this.lookupData.filter(f=> this.myWrittenValue.includes(f.itemId));
  }

  get visibleSelectedItems(): IAutoCompleteAddListModel[]{
    return this.selectedItems.filter(i => i.hiddenWhenSelected==null || i.hiddenWhenSelected==false);
  }

  get hasTemplate(): boolean
  {
    return this.itemTemplate != undefined;
  }

  // TODO: [Refactor][dpb][2023-03-06] the non string value could be handled more elegantly with 2 separate handlers of the value changes
  // TODO: [lw][2023-02-08] check solution with damien
  // see https://stackoverflow.com/questions/62022870/how-to-stop-material-dropdown-autocomplete-selection-from-triggering-another-sea
  protected _filter(value: any): IAutoCompleteAddListModel[] {
    // if(!this.lookupData) {
      if(!this.lookupData || typeof value !== 'string') {
      return [];
    }
    const availableForSelection = this.lookupData.filter((f) => !this.myWrittenValue.includes(f.itemId));
    return availableForSelection.filter((option) => option.itemName.toLowerCase().includes(value.toLowerCase()));
  }


  displaySelectedItem(option?: any) {
    return option ? option.itemName : '';
  }


  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // the value is set from the containing component
  writeValue(value: any): void {
    if (value) {
      this.myWrittenValue = value;
    } else {
      this.myWrittenValue = [];
    }
  }


  public validate() {
    return this.dataForm.valid;
  }

  listHasItems() {
    return this.myWrittenValue.length > 0;
  }

  add() {
    let selectedLookupItem = this.lookup.value;
    this.myWrittenValue.push(selectedLookupItem.itemId);
    this.lookup.setValue('');
    this.propagateChange(this.myWrittenValue);
    this.onTouched();
  }

  remove(itemId: number) {
    let existingItem = this.selectedItems.find((item) => item.itemId == itemId);

    if (existingItem && existingItem.canDelete) {
      if(this.deleteRequiresConfirmation) {
        this.confirmDelete(existingItem);
      } else {
        this.deleteItem(existingItem);
      }
    } else {
      this.snackBar.info(existingItem.cannotDeleteMessage);
    }
  }

  confirmDelete(item: any) {
    const dialogRef = this.dialog.open(ConfirmationModalComponent, {
      width: '450px',
      data: {
        title: 'Confirm Delete',
        message: this.deleteConfirmationMessage,
      },
    });

    dialogRef.componentInstance.onClose.subscribe((result) => {
      if (result.result) {
        this.deleteItem(item);
      }
    });
  }

  private deleteItem(item: any) {
    const index = this.myWrittenValue.indexOf(item.itemId);
    if (index > -1) { // only splice array when item is found
      this.myWrittenValue.splice(index, 1); // 2nd parameter means remove one item only
    }
    this.lookup.setValue(null);
    this.propagateChange(this.myWrittenValue);
    this.onTouched();
  }

  canAddOption(): boolean {
    return (this.lookup?.value && this.lookup.value.itemId) && this.allowAdd;
  }

  get noItemsToDisplayMessage(): string{
    return this.customNoItemsToDisplayMessage ?? "There are no Associated " + this.listItemTypeDisplay;
  }

  get listHeader(): string{
    return this.customListHeader ?? "Associated " + this.listItemTypeDisplaySingular + "s";
  }

  setFromParent( selectedItems: any[]): void {
    this.myWrittenValue = selectedItems;
    this.lookup.setValue(null);
  }

  //For use where forms use the TinyMCE Editor and the AutoCompleteAddListComponent.
  //When the dropdown addlist is expanded, clicking into the editor does not close the list
  //add (onFocus)="#AutoCompleteAddListComponent.closePanel()" to the editor to close the dropdown.
  closePanel() {
    this.autocomplete.closePanel();
  }

}

const customValueProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AutoCompleteAddListComponent),
  multi: true,
};
const customValidateProvider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => AutoCompleteAddListComponent),
  multi: true,
};

@Component({
    selector: 'auto-complete-add-list',
    templateUrl: './auto-complete-add-list.component.html',
    styleUrls: ['./auto-complete-add-list.component.scss'],
    providers: [customValueProvider, customValidateProvider],
    standalone: false
})
export class AutoCompleteAddListComponent extends AutoCompleteAddListComponentBase {}


